import React, { useState } from 'react';
import { BriefcaseIcon, CheckIcon, MapPinIcon, ClockIcon, XIcon, SendIcon, ChevronRightIcon, ListChecksIcon } from 'lucide-react';
type Job = {
  title: string;
  location: string;
  type: string;
  experience: string;
  description: string;
  responsibilities: string[];
  requirements: string[];
};
const CareersSection = () => {
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [selectedPosition, setSelectedPosition] = useState('');
  const jobs: Job[] = [{
    title: 'Civil Engineer',
    location: 'Mumbai',
    type: 'Full-time',
    experience: '6+ months',
    description: 'Oversee construction activities at our Virar West project site and ensure quality standards are met.',
    responsibilities: ['Supervise day-to-day construction activities on site', 'Ensure work is carried out as per approved drawings and specifications', 'Coordinate with contractors, vendors, and site workers', 'Monitor material quality and maintain site records', 'Report progress and flag issues to the Project Manager', 'Ensure compliance with safety standards and regulations'],
    requirements: ['Diploma or B.E. in Civil Engineering', 'Minimum 6 months of site experience', 'Knowledge of construction materials and methods', 'Good communication and coordination skills', 'Willingness to work on-site in Virar West, Mumbai']
  }, {
    title: 'Project Manager',
    location: 'Mumbai',
    type: 'Full-time',
    experience: '1+ year',
    description: 'Lead and manage real estate development projects from inception to completion, coordinating with contractors and stakeholders.',
    responsibilities: ['Plan, execute, and oversee all phases of the project lifecycle', 'Coordinate with architects, engineers, contractors, and vendors', 'Track project timelines, budgets, and deliverables', 'Manage approvals, permits, and regulatory compliance', 'Communicate project status to senior management and stakeholders', 'Identify risks and implement mitigation strategies'],
    requirements: ['B.E./B.Tech in Civil Engineering or related field', 'Minimum 1 year of experience in real estate or construction project management', 'Strong leadership and problem-solving skills', 'Proficiency in project management tools', 'Knowledge of MahaRERA and local building regulations is a plus']
  }];
  const benefits = ['Competitive salary and bonuses', 'Health and life insurance', 'Professional development opportunities', 'Work-life balance', 'Modern office environment', 'Team building activities'];
  const openModal = (job: Job) => {
    setSelectedJob(job);
    setSelectedPosition(job.title);
  };
  const closeModal = () => {
    setSelectedJob(null);
    setSelectedPosition('');
  };
  return <section id="careers" className="py-20 bg-gray-50" data-id="element-132">
      <div className="container mx-auto px-4 lg:px-8" data-id="element-133">
        {/* Section Header */}
        <div className="text-center mb-16" data-id="element-134">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-135">
            Careers at Swastik Infra
          </h2>
          <div className="w-20 h-1 bg-blue-800 mx-auto mb-6" data-id="element-136"></div>
          <p className="max-w-3xl mx-auto text-gray-600 text-lg" data-id="element-137">
            Join our team of professionals and build your career with one of
            India's leading real estate developers.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-138">
          {/* Why Join Us */}
          <div className="lg:col-span-1" data-id="element-139">
            <div className="bg-white p-8 rounded-xl shadow-sm h-full" data-id="element-140">
              <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-141">
                Why Join Us
              </h3>
              <p className="text-gray-600 mb-6" data-id="element-142">
                At Swastik Infra Developers, we believe in fostering talent and
                providing opportunities for growth. We are committed to creating
                an inclusive work environment where innovation thrives.
              </p>
              <div className="space-y-3" data-id="element-143">
                {benefits.map((benefit, index) => <div key={index} className="flex items-center gap-2" data-id="element-144">
                    <CheckIcon className="h-4 w-4 text-blue-800 flex-shrink-0" data-id="element-145" />
                    <p className="text-gray-700 text-sm" data-id="element-146">{benefit}</p>
                  </div>)}
              </div>
            </div>
          </div>

          {/* Current Openings */}
          <div className="lg:col-span-2 space-y-5" data-id="element-147">
            <h3 className="text-2xl font-bold text-gray-900" data-id="element-148">
              Current Openings
            </h3>
            {jobs.map((job, index) => <div key={index} className="bg-white rounded-xl shadow-sm p-6 border border-gray-100" data-id="element-149">
                <div className="flex flex-wrap justify-between items-start gap-3 mb-3" data-id="element-150">
                  <h4 className="text-xl font-bold text-gray-900" data-id="element-151">
                    {job.title}
                  </h4>
                  <span className="inline-flex items-center gap-1.5 bg-green-50 text-green-700 text-xs font-semibold px-3 py-1 rounded-full border border-green-200" data-id="element-152">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" data-id="element-153"></span>
                    Hiring Now
                  </span>
                </div>

                <div className="flex flex-wrap gap-4 text-sm text-gray-500 mb-4" data-id="element-154">
                  <div className="flex items-center gap-1" data-id="element-155">
                    <MapPinIcon className="h-4 w-4 text-blue-800" data-id="element-156" />
                    {job.location}
                  </div>
                  <div className="flex items-center gap-1" data-id="element-157">
                    <BriefcaseIcon className="h-4 w-4 text-blue-800" data-id="element-158" />
                    {job.type}
                  </div>
                  <div className="flex items-center gap-1" data-id="element-159">
                    <ClockIcon className="h-4 w-4 text-blue-800" data-id="element-160" />
                    {job.experience} experience
                  </div>
                </div>

                <p className="text-gray-600 text-sm mb-5" data-id="element-161">{job.description}</p>

                <button onClick={() => openModal(job)} className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-800 hover:bg-blue-900 text-white text-sm font-medium rounded-lg transition-all" data-id="element-162">
                  View Details & Apply
                  <ChevronRightIcon className="h-4 w-4" data-id="element-163" />
                </button>
              </div>)}
          </div>
        </div>
      </div>

      {/* Job Detail Modal */}
      {selectedJob && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={e => {
      if (e.target === e.currentTarget) closeModal();
    }} data-id="element-164">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl" data-id="element-165">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white z-10 flex items-start justify-between px-6 py-5 border-b border-gray-100" data-id="element-166">
              <div data-id="element-167">
                <h2 className="text-xl font-bold text-gray-900" data-id="element-168">
                  {selectedJob.title}
                </h2>
                <div className="flex flex-wrap gap-3 mt-2" data-id="element-169">
                  <span className="inline-flex items-center gap-1 text-xs text-gray-500" data-id="element-170">
                    <MapPinIcon className="h-3.5 w-3.5 text-blue-800" data-id="element-171" />
                    {selectedJob.location}
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs text-gray-500" data-id="element-172">
                    <BriefcaseIcon className="h-3.5 w-3.5 text-blue-800" data-id="element-173" />
                    {selectedJob.type}
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs text-gray-500" data-id="element-174">
                    <ClockIcon className="h-3.5 w-3.5 text-blue-800" data-id="element-175" />
                    {selectedJob.experience} experience
                  </span>
                </div>
              </div>
              <button onClick={closeModal} className="p-2 rounded-full hover:bg-gray-100 transition-colors flex-shrink-0" data-id="element-176">
                <XIcon className="h-5 w-5 text-gray-500" data-id="element-177" />
              </button>
            </div>

            <div className="px-6 py-6 space-y-6" data-id="element-178">
              {/* Description */}
              <div data-id="element-179">
                <p className="text-gray-600 leading-relaxed" data-id="element-180">
                  {selectedJob.description}
                </p>
              </div>

              {/* Responsibilities */}
              <div data-id="element-181">
                <h4 className="text-base font-bold text-gray-900 mb-3 flex items-center gap-2" data-id="element-182">
                  <ListChecksIcon className="h-4 w-4 text-blue-800" data-id="element-183" />
                  Key Responsibilities
                </h4>
                <ul className="space-y-2" data-id="element-184">
                  {selectedJob.responsibilities.map((item, idx) => <li key={idx} className="flex items-start gap-2 text-sm text-gray-600" data-id="element-185">
                      <ChevronRightIcon className="h-4 w-4 text-blue-800 flex-shrink-0 mt-0.5" data-id="element-186" />
                      {item}
                    </li>)}
                </ul>
              </div>

              {/* Requirements */}
              <div data-id="element-187">
                <h4 className="text-base font-bold text-gray-900 mb-3 flex items-center gap-2" data-id="element-188">
                  <CheckIcon className="h-4 w-4 text-blue-800" data-id="element-189" />
                  Requirements
                </h4>
                <ul className="space-y-2" data-id="element-190">
                  {selectedJob.requirements.map((item, idx) => <li key={idx} className="flex items-start gap-2 text-sm text-gray-600" data-id="element-191">
                      <CheckIcon className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" data-id="element-192" />
                      {item}
                    </li>)}
                </ul>
              </div>

              {/* Divider */}
              <div className="border-t border-gray-100 pt-6" data-id="element-193">
                <h4 className="text-base font-bold text-gray-900 mb-5" data-id="element-194">
                  Apply for this Position
                </h4>
                <form className="space-y-4" data-id="element-195">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-196">
                    <div data-id="element-197">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-198">
                        Full Name
                      </label>
                      <input type="text" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Your full name" data-id="element-199" />
                    </div>
                    <div data-id="element-200">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-201">
                        Email
                      </label>
                      <input type="email" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="you@email.com" data-id="element-202" />
                    </div>
                    <div data-id="element-203">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-204">
                        Phone
                      </label>
                      <input type="tel" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="+91 98765 43210" data-id="element-205" />
                    </div>
                    <div data-id="element-206">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-207">
                        Position
                      </label>
                      <input type="text" value={selectedPosition} readOnly className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-600" data-id="element-208" />
                    </div>
                    <div data-id="element-209">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-210">
                        Years of Experience
                      </label>
                      <input type="number" min="0" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="e.g. 2" data-id="element-211" />
                    </div>
                    <div data-id="element-212">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-213">
                        Upload Resume
                      </label>
                      <input type="file" accept=".pdf,.doc,.docx" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-214" />
                    </div>
                  </div>
                  <div data-id="element-215">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-216">
                      Cover Letter
                    </label>
                    <textarea rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Tell us why you're a great fit..." data-id="element-217" />
                  </div>
                  <div className="flex gap-3 pt-2" data-id="element-218">
                    <button type="submit" className="flex-1 inline-flex items-center justify-center gap-2 py-3 bg-blue-800 hover:bg-blue-900 text-white font-medium rounded-lg transition-all text-sm" data-id="element-219">
                      <SendIcon className="h-4 w-4" data-id="element-220" />
                      Submit Application
                    </button>
                    <button type="button" onClick={closeModal} className="px-5 py-3 border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium rounded-lg transition-all text-sm" data-id="element-221">
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>}
    </section>;
};
export default CareersSection;